import { createSelector } from 'reselect';
import { urlWithLocale, languageForLocale } from '../utils/locale';

const MAX_CITY_FILTERS = 5;
const MAX_REVIEWS_PER_RESTAURANT = 3;

const getCuisineName = (state) => state.cuisine.cuisine || '';
const getCity = (state) => state.cuisine.city || '';
const getAlternateLocales = (state) => state.cuisine.alternateLocales || [];

const getTld = (state) => state.request.country.tld;
const getHostname = (state) => state.request.host;
const getIsoCode = (state) => state.request.country.isoCode;

const getIsPostcodeCountry = (state) => state.request.country.postcodeSearch;

const getCityFilterOptions = (state) =>
  state.cuisine.cities.slice(0, MAX_CITY_FILTERS).map(({ slug, displayName }) => ({
    compareValue: slug,
    displayName,
  }));

const getCities = (state) => state.cuisine.cities;

const getRestaurantReviews = (state) => {
  const isCityPage = Boolean(state.cuisine.city);
  const cityFilterSlugs = state.cuisine.cities.slice(0, MAX_CITY_FILTERS).map(({ slug }) => slug);

  const filteredRestaurantReviews =
    isCityPage || cityFilterSlugs.length === 0
      ? state.cuisine.restaurantReviews
      : state.cuisine.restaurantReviews.filter(({ restaurant }) =>
          // only show reviews for restaurants where the city
          // matches one of our city filter options
          cityFilterSlugs.some((slug) => slug === restaurant.city),
        );

  return filteredRestaurantReviews.map(({ reviews, restaurant }) => ({
    restaurant,
    reviews: reviews.slice(0, MAX_REVIEWS_PER_RESTAURANT),
  }));
};

const getUrlTemplate = (state) => {
  const isCityPage = !!getCity(state);
  const cuisineName = getCuisineName(state);

  const { host, country, locale, currentQuery = {} } = state.request;

  const urlTemplate = isCityPage
    ? urlWithLocale(
        country,
        `/restaurants/${currentQuery.city}/:location?tags=${cuisineName.toLowerCase()}`,
        locale,
      )
    : urlWithLocale(country, `/cuisines/${currentQuery.cuisine}/:location`, locale);

  return `${host}${urlTemplate}`;
};

const getLocations = (state) => {
  const locations = state.cuisine.city ? state.cuisine.neighborhoods : state.cuisine.cities;
  const urlTemplate = getUrlTemplate(state);

  return locations.map(({ displayName, slug }) => ({
    name: displayName,
    url: urlTemplate.replace(':location', slug),
  }));
};

const getCurrentQuery = (state) => state.request.currentQuery || {};

export const restaurantReviewsSelector = createSelector(
  [
    getCuisineName,
    getCity,
    getTld,
    getIsPostcodeCountry,
    getCityFilterOptions,
    getRestaurantReviews,
  ],
  (cuisineName, city, tld, isPostcodeCountry, cityFilterOptions, restaurantReviews) => ({
    cuisine: cuisineName,
    city,
    tld,
    isPostcodeCountry,
    cityFilterOptions,
    restaurantReviews,
  }),
);

export const locationSelector = createSelector([getLocations, getCity], (locations, city) => ({
  locations,
  isCityPage: !!city,
}));

export const alternateLocaleUrlsSelector = createSelector(
  [getHostname, getIsoCode, getAlternateLocales],
  (hostname, isoCode, alternateLocales) =>
    alternateLocales.map(({ locale, cuisine, city }) => ({
      href: `${hostname}/${locale.toLowerCase()}/cuisines/${cuisine}${city ? `/${city}` : ''}`,
      hrefLang: languageForLocale(locale, isoCode),
    })),
);

export const isSingleCityMarketSelector = createSelector(
  [getCities],
  (cities) => !(cities.length > 1),
);

export const hasQueryParamsSelector = createSelector([getCurrentQuery], (currentQuery) => {
  // 'cuisine', 'city' are returned in request.currentQuery, we want to ignore these.
  // any additional params in the object will come from a query string.
  const WHITELIST_PARAMS = ['locale', 'cuisine', 'city'];
  const queryParams = Object.keys(currentQuery).filter(
    (param) => !WHITELIST_PARAMS.includes(param),
  );

  return Boolean(queryParams.length);
});
